import { Loader2, CheckCircle2, Circle, XCircle, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';

export interface EnviarBKOStep {
  id: number;
  label: string;
  status: 'pending' | 'loading' | 'done' | 'error';
}

interface Props {
  isOpen: boolean;
  currentStep: number;
  steps: EnviarBKOStep[];
  runId: string;
  elapsedMs: number;
  errorMessage?: string | null;
  onRetry?: () => void;
  onClose?: () => void;
}

export function EnviarBKOOverlay({
  isOpen,
  currentStep,
  steps,
  runId,
  elapsedMs,
  errorMessage,
  onRetry,
  onClose,
}: Props) {
  if (!isOpen) return null;

  const hasError = !!errorMessage;
  const progress = hasError ? 0 : Math.min(((currentStep) / steps.length) * 100, 100);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-[540px] max-w-[95vw] rounded-2xl bg-background p-8 shadow-2xl border border-border">
        {/* Header */}
        <div className="flex items-start gap-4">
          {hasError ? (
            <XCircle className="h-10 w-10 text-destructive shrink-0" />
          ) : (
            <Loader2 className="h-10 w-10 animate-spin text-primary shrink-0" />
          )}
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-foreground">
              {hasError ? 'Erro ao enviar pedido' : 'Enviando seu pedido ao BKO'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {hasError
                ? 'Ocorreu um problema durante o processamento.'
                : 'Não feche esta tela. Estamos processando com segurança.'
              }
            </p>
          </div>
        </div>

        {/* Barra de Progresso */}
        <div className="mt-6">
          <Progress 
            value={progress} 
            className="h-2"
            indicatorClassName={cn(
              hasError ? 'bg-destructive' : 'bg-primary'
            )}
          />
        </div>

        {/* Checklist de Etapas */}
        <div className="mt-6 space-y-3">
          {steps.map((step) => (
            <div
              key={step.id}
              className={cn(
                'flex items-center gap-3 text-sm transition-colors duration-200',
                step.status === 'done' && 'text-success',
                step.status === 'error' && 'text-destructive',
                step.status === 'loading' && 'text-primary font-medium',
                step.status === 'pending' && 'text-muted-foreground'
              )}
            >
              {step.status === 'done' && (
                <CheckCircle2 className="h-5 w-5 shrink-0" />
              )}
              {step.status === 'loading' && (
                <Loader2 className="h-5 w-5 animate-spin shrink-0" />
              )}
              {step.status === 'error' && (
                <XCircle className="h-5 w-5 shrink-0" />
              )}
              {step.status === 'pending' && (
                <Circle className="h-5 w-5 shrink-0" />
              )}
              <span>{step.label}</span>
            </div>
          ))}
        </div>

        {/* Mensagem de Erro */}
        {hasError && (
          <div className="mt-6 rounded-lg bg-destructive/10 border border-destructive/20 p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-destructive">O que aconteceu:</p>
                <p className="text-destructive/80 mt-1">{errorMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Debug Info */}
        <div className="mt-6 rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground font-mono">
          <div className="flex justify-between">
            <span><strong>ID:</strong> {runId}</span>
            <span><strong>Tempo:</strong> {Math.round(elapsedMs / 1000)}s</span>
          </div>
        </div>

        {/* Botões de Ação (só em caso de erro) */}
        {hasError && (
          <div className="mt-6 flex gap-3">
            {onRetry && (
              <Button onClick={onRetry} className="flex-1">
                Tentar novamente
              </Button>
            )}
            {onClose && (
              <Button onClick={onClose} variant="outline" className="flex-1">
                Fechar
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
